import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { PageTemplate } from '@_widgets/common';
import { useContentCreateQuitPopup, CONTENT_QUERY_KEYS } from '@_features/content';

import { useFolderListQuery } from '@_features/folder';
import { contentApi, CONTENT_CONSTANTS } from '@_entities/content';

import { LeftChevronIcon } from '@_shared/icons';
import { toastGenerate } from '@_shared/libs';
import { SkeletonUI } from '@_shared/ui';
import { Button } from '@_shared/ui/Button';
import { Typography } from '@_shared/ui/Typography';

import {
	isCheckedFolder,
	isURLNotEntered,
	isFolderChecked,
	isMemoEntered,
} from './CreatePage.logic';
import * as S from './CreatePage.style';

const URL_SCHEMA = z.string().url();

const CHECKED_FOLDER_TEXT_COLOR = 'warmgray-800';
const UNCHECKED_FOLDER_TEXT_COLOR = 'warmgray-600';
const BUTTON_ACTIVE_COLOR = 'orange-primary';
const BUTTON_INACTIVE_COLOR = 'warmgray-300';

export function CreatePage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { isLoading, data: folderlist } = useFolderListQuery('manual');

	const [URL, setURL] = useState<string>('');
	const [checkedFolderState, setCheckedFolderState] = useState<number>(-1);
	const [memoLength, setMemoLength] = useState<number>(0);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const { openContentCreateQuitPopup } = useContentCreateQuitPopup();

	function isButtonActive() {
		return checkedFolderState !== -1 && URL !== '';
	}

	return (
		<PageTemplate
			header={
				<S.HeaderContainer>
					<S.BackIconWrapper
						onClick={() => {
							// 페이지 내 어떤한 수정이라도 있다면? -> "해당 페이지를 나가시겠습니까? 팝업" 발생
							if (
								isURLNotEntered(URL.length) &&
								!isFolderChecked(checkedFolderState) &&
								isMemoEntered(memoLength)
							) {
								navigate(-1);
							} else {
								openContentCreateQuitPopup();
							}
						}}
					>
						<LeftChevronIcon />
					</S.BackIconWrapper>
				</S.HeaderContainer>
			}
			nav={false}
		>
			<S.Form
				onSubmit={(e) => {
					e.preventDefault();

					if (!URL_SCHEMA.safeParse(URL).success) {
						toastGenerate({
							message: '링크가 올바르지 않아요. 다시 확인해 주세요!',
						});
					} else {
						const formData = new FormData(e.currentTarget);

						contentApi.create({
							URL,
							folderId: checkedFolderState,
							memo: formData.get('memo') as string,
						});
						toastGenerate({
							message: '링크를 추가했어요. 저장은 잠시 후 완료돼요!',
						});

						queryClient.invalidateQueries({
							queryKey: CONTENT_QUERY_KEYS.contentList(String(checkedFolderState)),
						});
						queryClient.invalidateQueries({
							queryKey: CONTENT_QUERY_KEYS.contentList('all'),
						});

						navigate(-1);
					}
				}}
			>
				{/* 콘텐츠 입력 */}
				<S.FormItem>
					<Typography type="title-large" color="font-secondary">
						링크
					</Typography>
					<S.Input
						name="URL"
						placeholder="링크를 입력해 주세요!"
						type="text"
						required
						onChange={(e) => {
							setURL(e.currentTarget.value);
						}}
					/>
				</S.FormItem>

				{/* 폴던 선택 */}
				<S.FormItem>
					<Typography type="title-large" color="font-secondary">
						폴더 선택
					</Typography>

					<S.FolderList>
						{/* Skeleton UI */}
						{isLoading ? (
							<>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
								<S.FolderItem isChecked={false}>
									<SkeletonUI width="100%" height="100%" borderRadius="8px" />
								</S.FolderItem>
							</>
						) : (
							<>
								{folderlist?.map((folder, index) => {
									if (index < 12 || (index >= 12 && isOpen)) {
										return (
											<S.FolderItem
												key={`${folder.id} folder button`}
												htmlFor={`${folder.id}`}
												isChecked={isCheckedFolder(
													checkedFolderState,
													folder.id,
												)}
											>
												<Typography
													type="body-large"
													color={
														isCheckedFolder(
															checkedFolderState,
															folder.id,
														)
															? CHECKED_FOLDER_TEXT_COLOR
															: UNCHECKED_FOLDER_TEXT_COLOR
													}
												>
													{folder.name}
												</Typography>
												<input
													type="radio"
													name="folderId"
													value={`${folder.id}`}
													id={`${folder.id}`}
													onInput={() => {
														setCheckedFolderState(folder.id);
													}}
												/>
											</S.FolderItem>
										);
									}
									return <></>;
								})}
							</>
						)}
					</S.FolderList>
					{folderlist?.length && folderlist.length > 12 && (
						<S.FolderListExpandButton
							type="button"
							onClick={() => {
								setIsOpen((prev) => !prev);
							}}
						>
							<span>{isOpen ? '접기' : '더보기'}</span>
							{isOpen ? (
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="10"
									height="6"
									viewBox="0 0 10 6"
									fill="none"
								>
									<path
										d="M1.25 5L4.75 1L8.75 5"
										stroke="#504C49"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							) : (
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="9"
									height="6"
									viewBox="0 0 9 6"
									fill="none"
								>
									<path
										d="M0.75 1L4.25 5L8.25 1"
										stroke="#504C49"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							)}
						</S.FolderListExpandButton>
					)}
				</S.FormItem>

				{/* 메모 입력 */}
				<S.FormItem>
					<Typography type="title-large" color="font-secondary">
						메모
					</Typography>
					<S.TextArea
						placeholder="키워드로 메모해두면 쉽게 찾아볼 수 있어요!"
						name="memo"
						maxLength={CONTENT_CONSTANTS.MEMO_MAX_LENGTH}
						onInput={(e) => {
							setMemoLength(e.currentTarget.value.length);
						}}
					/>
					<S.MemoLengthContainer>
						<Typography type="label-large" color="warmgray-400">
							{memoLength}/{CONTENT_CONSTANTS.MEMO_MAX_LENGTH}
						</Typography>
					</S.MemoLengthContainer>
				</S.FormItem>

				{/* Footer */}
				<S.Footer>
					<Button
						type="submit"
						fullWidth
						bgColor={isButtonActive() ? BUTTON_ACTIVE_COLOR : BUTTON_INACTIVE_COLOR}
						disabled={!isButtonActive()}
					>
						<Typography type="title-large" color="white-primary">
							저장
						</Typography>
					</Button>
				</S.Footer>
			</S.Form>
		</PageTemplate>
	);
}
